import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {SmartDateTimeFormatterContainer} from '../../../../../srcCommon/formatters/dateTime/smartDateTimeFormatterContainer';
import {getValidTimestampFromValue} from '../../../../helpers/dateTimeHelpers';
import {
  AccountAttributeUpdateAction,
  addCustomAttribute,
  deleteCustomAttribute,
  updateCustomAttribute,
} from '../../../../helpers/models/accountAttributeUpdateHelpers';
import {CrmSyncStatusesEnum} from '../../../../models/api/crmSyncModel';
import {CustomFieldModel, CustomFieldsEnum} from '../../../../models/api/customFieldModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {LinkifyFormatter} from '../../../common/formatters/markdown/linkifyFormatter';
import {InlineEditingBooleanPopover} from '../../../common/inlineEditing/inlineEditingBooleanPopover';
import {InlineEditingDateTimePickerContainer} from '../../../common/inlineEditing/inlineEditingDateTimePickerContainer';
import {InlineEditingInboxPopover} from '../../../common/inlineEditing/inlineEditingInboxPopover';
import {InlineEditingNumberInputBox} from '../../../common/inlineEditing/inlineEditingNumberInputBox';
import {InlineEditingTeammatePopover} from '../../../common/inlineEditing/inlineEditingTeammatePopover';
import {InlineEditingTextBox} from '../../../common/inlineEditing/inlineEditingTextBox';
import {InlineEditingProps} from '../../../common/inlineEditing/useInlineEditing';
import {LinkTrap} from '../../../common/linkTrap/linkTrap';
import {CustomAttributeInboxContainer} from '../../../contacts/contactInformation/customAttributeInboxContainer';
import {CustomAttributeTeammateContainer} from '../../../contacts/contactInformation/customAttributeTeammateContainer';
import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
  buildAccountDetailsEditPayload,
} from '../../tracking/accountDetailsTrackingHelpers';
import {useAccountDetailsContext} from '../accountDetailsContext';
import {AccountInformationAttribute} from './accountInformationAttribute';

/*
 * Props.
 */

export interface CustomAttributeModel {
  customField: CustomFieldModel;
  value: string;
}

interface CustomAttributeProps {
  accountId: number;
  customAttribute: CustomAttributeModel;
  isFieldCrmSyncing: boolean;
  crmSyncEditLink?: string;
  crmSyncStatus?: CrmSyncStatusesEnum;
  crmSyncSource?: string;
  shouldDisableActions?: boolean;
  updateAccountAttributeAsync: AsyncHandlerOf<AccountAttributeUpdateAction>;
  updateUnsavedAccount: HandlerOf<AccountAttributeUpdateAction>;
}

/*
 * Style.
 */

const StyledValueDiv = styled.div`
  margin: 8px 0 7px 0;
`;

const StyledDatetimeValueDiv = styled.div`
  display: flex;
`;

const StyledCalendarIcon = styled(Icon)`
  margin-right: 8px;
`;

/*
 * Components.
 */

export const AccountCustomAttribute: FC<CustomAttributeProps> = (props) => {
  const {
    accountId,
    customAttribute,
    isFieldCrmSyncing,
    crmSyncEditLink,
    crmSyncSource,
    crmSyncStatus,
    shouldDisableActions,
    updateAccountAttributeAsync,
    updateUnsavedAccount,
  } = props;
  const isAddingNewCustomAttribute = customAttribute.value === '';
  const {onTrackEvent} = useAccountDetailsContext();

  return (
    <AccountInformationAttribute
      title={customAttribute.customField.name}
      currentAttributeValue={customAttribute.value}
      isFieldCrmSyncing={isFieldCrmSyncing}
      crmSyncEditLink={crmSyncEditLink}
      crmSyncStatus={crmSyncStatus}
      crmSyncSource={crmSyncSource}
      shouldDisableActions={shouldDisableActions}
      renderValue={(inlineEditingProps) => {
        if (inlineEditingProps.isEditing) {
          return renderInlineEditingInput(customAttribute, inlineEditingProps);
        }

        if (isAddingNewCustomAttribute) {
          return <StyledValueDiv>-</StyledValueDiv>;
        }

        return <StyledValueDiv>{renderCustomAttributeValue(customAttribute)}</StyledValueDiv>;
      }}
      onSave={(newValue: string) => {
        const action = computeOnSaveAction(
          isAddingNewCustomAttribute,
          customAttribute.value,
          customAttribute.customField,
          newValue,
        );
        onTrackEvent(
          buildAccountDetailsEditPayload(
            accountId,
            isAddingNewCustomAttribute
              ? AccountDetailsEditActionsEnum.ADD
              : AccountDetailsEditActionsEnum.EDIT,
            AccountDetailsEditAttributesEnum.CUSTOM_FIELD,
          ),
        );
        return updateAccountAttributeAsync(action);
      }}
      onDelete={() => {
        const action = deleteCustomAttribute(customAttribute.value, customAttribute.customField);

        // No need to hit the backend if the user deletes the custom attribute before it's saved
        if (isAddingNewCustomAttribute) {
          return updateUnsavedAccount(action);
        }

        onTrackEvent(
          buildAccountDetailsEditPayload(
            accountId,
            AccountDetailsEditActionsEnum.DELETE,
            AccountDetailsEditAttributesEnum.CUSTOM_FIELD,
          ),
        );
        return updateAccountAttributeAsync(action);
      }}
      shouldStartInEditMode={isAddingNewCustomAttribute}
    />
  );
};

function renderCustomAttributeValue(customAttribute: CustomAttributeModel) {
  const {value, customField} = customAttribute;
  switch (customField.type) {
    case CustomFieldsEnum.STRING:
      // User sometimes enter links - this lets those links be clickable.
      return (
        <LinkTrap>
          <LinkifyFormatter text={value} />
        </LinkTrap>
      );

    case CustomFieldsEnum.DATETIME:
      return (
        <UserThemeConsumer>
          {(theme) => (
            <StyledDatetimeValueDiv>
              <StyledCalendarIcon name="calendarSmall" color={theme.greys.shade70} />
              <SmartDateTimeFormatterContainer value={getValidTimestampFromValue(value)} />
            </StyledDatetimeValueDiv>
          )}
        </UserThemeConsumer>
      );

    case CustomFieldsEnum.BOOLEAN:
      return value === 'true' ? (
        <Trans id="account-details-custom-attribute-value-yes">Yes</Trans>
      ) : (
        <Trans id="account-details-custom-attribute-value-no">No</Trans>
      );

    case CustomFieldsEnum.TEAMMATE:
      return <CustomAttributeTeammateContainer teammateEmail={customAttribute.value} />;

    case CustomFieldsEnum.INBOX:
      return <CustomAttributeInboxContainer inboxPublicId={customAttribute.value} />;

    default:
      return value;
  }
}

function renderInlineEditingInput(
  customAttribute: CustomAttributeModel,
  inlineEditingProps: InlineEditingProps,
) {
  const {customField} = customAttribute;
  const {editedValue, onEdit, onEditSave, onEditCancel} = inlineEditingProps;

  switch (customField.type) {
    case CustomFieldsEnum.STRING:
      return (
        <InlineEditingTextBox
          value={editedValue}
          onEdit={onEdit}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
        />
      );

    case CustomFieldsEnum.DATETIME:
      return (
        <InlineEditingDateTimePickerContainer
          value={editedValue}
          onEdit={onEdit}
          onEditSave={onEditSave}
          selectedValue={customAttribute.value}
        />
      );

    case CustomFieldsEnum.BOOLEAN:
      return (
        <InlineEditingBooleanPopover
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
          value={customAttribute.value}
        />
      );

    case CustomFieldsEnum.NUMBER:
      return (
        <InlineEditingNumberInputBox
          value={editedValue}
          onEdit={onEdit}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
        />
      );

    case CustomFieldsEnum.TEAMMATE:
      return (
        <InlineEditingTeammatePopover
          onEditSave={(teammate: TeammateModel) => onEditSave(teammate.email)}
          onEditCancel={onEditCancel}
          teammateEmail={customAttribute.value}
          renderButtonContent={(teammateEmail: string) => (
            <CustomAttributeTeammateContainer teammateEmail={teammateEmail} />
          )}
        />
      );

    case CustomFieldsEnum.INBOX:
      return (
        <InlineEditingInboxPopover
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
          inboxPublicId={customAttribute.value}
          renderButtonContent={(inboxPublicId: string) => (
            <CustomAttributeInboxContainer inboxPublicId={inboxPublicId} />
          )}
        />
      );

    default:
      return null;
  }
}

function computeOnSaveAction(
  isAddingNewCustomAttribute: boolean,
  currentValue: string,
  customField: CustomFieldModel,
  newValue: string,
) {
  if (isAddingNewCustomAttribute) {
    return addCustomAttribute(newValue, customField);
  }

  // If the user empties the field, treat it as a delete
  if (!newValue) {
    return deleteCustomAttribute(currentValue, customField);
  }

  return updateCustomAttribute(currentValue, newValue, customField);
}
